import {
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Localization } from "@viamap/viamap2-common";
import { GlassButton } from "src/components/GlassButtons";
import {
  AdvancedDragModal,
  ADModalBody,
  ADModalFooter,
} from "src/componentsUtils/AdvancedDragModal";
import { ExportData } from "src/managers/ExportData";
import { ReferenceGeomDenmark } from "src/managers/ReferenceGeomDenmark";
import { SheetFunc } from "src/managers/SheetFunc";
import { MapitStateContext } from "src/states/MapitState";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { JPBPriceIndexSource } from "./JPBState";
import {
  JPBManager,
  type JPBPriceIndexFactorsTable,
  type JPBPriceIndexLandsdelFactorRow,
} from "./JPBManager";
import { renderJPBLandsdelGeometryImage } from "./JPBMapSnapshot";
import JPBRegions from "./JPBRegions.json";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type VisualizationMode = "factorIndex" | "valueDkkHa";

type ChartRow = {
  periodKey: string;
  [landsdel: string]: string | number | null;
};

type MunicipalityInfo = {
  kommunenr: number;
  kommunenavn: string;
};

type GeometryPreviewState = {
  loading: boolean;
  error: string | null;
  imageUrl: string | null;
};

type JPBPriceIndexSourceWindowParams = {
  activeLandsdele?: string[];
};

const chartStrokePalette = [
  "#0f6c81",
  "#c45135",
  "#2f8f70",
  "#725ea8",
  "#a77916",
  "#c23d7a",
  "#4f7fbd",
  "#8a5a44",
  "#6f7f2a",
  "#00796b",
  "#7b1fa2",
];

const modes: Array<{
  mode: VisualizationMode;
  labelKey: string;
  descriptionKey: string;
}> = [
  {
    mode: "factorIndex",
    labelKey: "Index 100",
    descriptionKey: "info:jpbIndexModeIndex100",
  },
  {
    mode: "valueDkkHa",
    labelKey: "DKK/ha",
    descriptionKey: "info:jpbIndexModeDkkHa",
  },
];

const styles: Record<string, CSSProperties> = {
  shell: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: 16,
    maxHeight: "calc(100vh - 150px)",
    overflowY: "auto",
    color: "#1f2c2c",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "minmax(280px, 1fr) auto",
    gap: 12,
    alignItems: "start",
    padding: 12,
    borderRadius: 18,
    background:
      "linear-gradient(135deg, rgba(231,240,237,0.98), rgba(250,250,247,0.98))",
    border: "1px solid rgba(47,83,78,0.18)",
    boxShadow: "0 14px 35px rgba(18,43,39,0.08)",
  },
  eyebrow: {
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#486863",
    fontWeight: 700,
  },
  title: {
    margin: "2px 0 4px",
    fontSize: 20,
    lineHeight: 1.15,
    color: "#1f3935",
    fontWeight: 800,
  },
  infoText: {
    margin: 0,
    color: "#425653",
    lineHeight: 1.35,
    fontSize: 12,
  },
  toolbar: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  panel: {
    borderRadius: 16,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(47,83,78,0.16)",
    boxShadow: "0 10px 24px rgba(18,43,39,0.06)",
    overflow: "hidden",
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    borderBottom: "1px solid rgba(47,83,78,0.12)",
    background: "rgba(244,248,246,0.95)",
  },
  chartWrap: {
    overflowX: "auto",
    flex: "0 0 auto",
  },
  chartCanvas: {
    minWidth: 980,
    height: 360,
    minHeight: 360,
    padding: "12px 14px 10px 4px",
  },
  modeSwitch: {
    display: "inline-flex",
    padding: 4,
    gap: 4,
    borderRadius: 999,
    background: "rgba(47,83,78,0.10)",
  },
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 300px",
    gap: 16,
    alignItems: "start",
  },
  tableTools: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "12px 14px",
    borderBottom: "1px solid rgba(47,83,78,0.12)",
    background: "rgba(244,248,246,0.8)",
  },
  tableScroll: {
    overflowX: "auto",
    overflowY: "visible",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 12,
    color: "#263b38",
  },
  sideCard: {
    padding: 14,
    borderRadius: 16,
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(47,83,78,0.16)",
    boxShadow: "0 10px 24px rgba(18,43,39,0.06)",
  },
};

function getLocale() {
  return Localization.getLocale();
}

function formatDkk(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "-";
  return `${new Intl.NumberFormat(getLocale(), {
    maximumFractionDigits: 0,
  }).format(value)} ${Localization.getText("currency:dkkSuffix")}`;
}

function formatCellValue(
  value: number | null | undefined,
  mode: VisualizationMode,
): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "-";
  if (mode === "valueDkkHa") return formatDkk(value);
  return value.toFixed(1);
}

function getModeValue(
  row: JPBPriceIndexLandsdelFactorRow,
  periodKey: string,
  mode: VisualizationMode,
): number | null {
  if (mode === "valueDkkHa") {
    const value = row.valuesByPeriod[periodKey];
    return typeof value === "number" && Number.isFinite(value)
      ? value * 10000
      : null;
  }

  const factor = row.factorsByPeriod[periodKey];
  if (typeof factor !== "number" || !Number.isFinite(factor)) return null;
  return factor * 100;
}

function hasAnyValue(
  row: JPBPriceIndexLandsdelFactorRow,
  periodKeys: string[],
  mode: VisualizationMode,
): boolean {
  return periodKeys.some((periodKey) => {
    const value = getModeValue(row, periodKey, mode);
    return typeof value === "number" && Number.isFinite(value);
  });
}

function formatYAxisTick(value: number, mode: VisualizationMode): string {
  if (mode === "factorIndex") return value.toFixed(0);
  return new Intl.NumberFormat(getLocale(), {
    notation: "compact",
    maximumFractionDigits: 0,
  }).format(value);
}

function getYAxisDomain(
  rows: JPBPriceIndexLandsdelFactorRow[],
  periodKeys: string[],
  mode: VisualizationMode,
): [number, number] {
  const values = rows.flatMap((row) =>
    periodKeys
      .map((periodKey) => getModeValue(row, periodKey, mode))
      .filter(
        (value): value is number =>
          typeof value === "number" && Number.isFinite(value),
      ),
  );
  if (mode === "factorIndex") values.push(100);
  if (!values.length) return mode === "factorIndex" ? [90, 110] : [0, 1];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const minimumSpread =
    mode === "factorIndex" ? 20 : Math.max(Math.abs(max) * 0.16, 1);
  const spread = Math.max(max - min, minimumSpread);
  const pad = spread * (mode === "factorIndex" ? 0.35 : 0.3);
  return [Math.floor((min - pad) * 10) / 10, Math.ceil((max + pad) * 10) / 10];
}

function getRequiredYAxisTicks(
  domain: [number, number],
  mode: VisualizationMode,
): number[] | undefined {
  if (mode !== "factorIndex") return undefined;

  const [min, max] = domain;
  const spread = Math.max(max - min, 1);
  const rawStep = spread / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceStep =
    (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) *
    magnitude;

  const ticks: number[] = [];
  for (
    let tick = Math.ceil(min / niceStep) * niceStep;
    tick <= max + niceStep * 0.001;
    tick += niceStep
  ) {
    ticks.push(Number(tick.toFixed(6)));
  }

  if (!ticks.some((tick) => Math.abs(tick - 100) < 0.000001)) ticks.push(100);
  return ticks.sort((a, b) => a - b);
}

function isYearStartQuarter(periodKey: string): boolean {
  return /^\d{4}K1$/i.test(periodKey);
}

function getMunicipalitiesByLandsdel(): Map<string, MunicipalityInfo[]> {
  const map = new Map<string, MunicipalityInfo[]>();
  const municipalities = (JPBRegions as any).municipalities as Array<{
    landsdel?: string;
    kommunenr?: number;
    kommunenavn?: string;
  }>;
  municipalities.forEach((municipality) => {
    if (
      !municipality.landsdel ||
      !municipality.kommunenavn ||
      municipality.kommunenr === undefined
    )
      return;
    const landsdel = JPBManager.getMolbakDisplayLandsdel(municipality.landsdel);
    const items = map.get(landsdel) ?? [];
    items.push({
      kommunenr: municipality.kommunenr,
      kommunenavn: municipality.kommunenavn,
    });
    map.set(landsdel, items);
  });
  map.forEach((items) =>
    items.sort((a, b) => a.kommunenavn.localeCompare(b.kommunenavn, "da")),
  );
  return map;
}

function getMunicipalityFeatures(kommunenrList: number[]): any[] {
  const refGeo = ReferenceGeomDenmark.getReferenceGeo();
  const features = (refGeo as any)?.municipality?.features;
  if (!Array.isArray(features)) return [];
  const codeSet = new Set(kommunenrList.map((code) => String(code)));
  return features.filter((feature) =>
    codeSet.has(
      String(feature?.properties?.Komnr ?? feature?.properties?.KOMKODE ?? ""),
    ),
  );
}

export function JPBPriceIndexSourceWindow(props: {
  managedWindow: ManagedWindow;
}) {
  const { dispatch: windowDispatch } = useWindowContext();
  const { state: mapitState } = useContext(MapitStateContext);
  const params = props.managedWindow.windowParams as
    | JPBPriceIndexSourceWindowParams
    | undefined;
  const activeLandsdele = useMemo(
    () => params?.activeLandsdele ?? [],
    [params?.activeLandsdele],
  );
  const mapStyle = mapitState.mapInfo?.mapStyle;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [table, setTable] = useState<JPBPriceIndexFactorsTable | null>(null);
  const [visibleLandsdele, setVisibleLandsdele] = useState<string[]>([]);
  const [mode, setMode] = useState<VisualizationMode>("factorIndex");
  const [infoLandsdel, setInfoLandsdel] = useState<string | null>(null);
  const [geometryPreview, setGeometryPreview] = useState<GeometryPreviewState>({
    loading: false,
    error: null,
    imageUrl: null,
  });

  const municipalitiesByLandsdel = useMemo(
    () => getMunicipalitiesByLandsdel(),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    JPBManager.getPriceIndexFactorsTable(JPBPriceIndexSource.Molbak)
      .then((molbakTable) => {
        if (cancelled) return;
        const activeSet = new Set(activeLandsdele);
        const defaultVisible = molbakTable.rows
          .filter(
            (row) =>
              activeSet.has(row.landsdel) &&
              hasAnyValue(row, molbakTable.periodKeys, "factorIndex"),
          )
          .map((row) => row.landsdel);
        setTable(molbakTable);
        setVisibleLandsdele(defaultVisible);
        setInfoLandsdel(
          defaultVisible[0] ?? molbakTable.rows[0]?.landsdel ?? null,
        );
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.message || String(err));
        setTable(null);
        setVisibleLandsdele([]);
        setInfoLandsdel(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeLandsdele, props.managedWindow.updateCounter]);

  const visibleRows = useMemo(() => {
    if (!table) return [];
    const visibleSet = new Set(visibleLandsdele);
    return table.rows.filter((row) => visibleSet.has(row.landsdel));
  }, [table, visibleLandsdele]);

  const tableRows = useMemo(() => table?.rows ?? [], [table?.rows]);

  const chartRows = useMemo<ChartRow[]>(() => {
    if (!table) return [];
    return table.periodKeys.map((periodKey) => {
      const chartRow: ChartRow = { periodKey };
      table.rows.forEach((landsdelRow) => {
        chartRow[landsdelRow.landsdel] = getModeValue(
          landsdelRow,
          periodKey,
          mode,
        );
      });
      return chartRow;
    });
  }, [mode, table]);

  const xTicks = useMemo(() => {
    const periodKeys = table?.periodKeys ?? [];
    const yearStartTicks = periodKeys.filter(isYearStartQuarter);
    return yearStartTicks.length ? yearStartTicks : periodKeys;
  }, [table?.periodKeys]);

  const yAxisDomain = useMemo(
    () => getYAxisDomain(visibleRows, table?.periodKeys ?? [], mode),
    [mode, table?.periodKeys, visibleRows],
  );
  const yAxisTicks = useMemo(
    () => getRequiredYAxisTicks(yAxisDomain, mode),
    [mode, yAxisDomain],
  );
  const selectedInfoRow = useMemo(
    () =>
      table?.rows.find((row) => row.landsdel === infoLandsdel) ??
      table?.rows[0] ??
      null,
    [infoLandsdel, table?.rows],
  );
  const selectedMunicipalities = selectedInfoRow
    ? (municipalitiesByLandsdel.get(selectedInfoRow.landsdel) ?? [])
    : [];
  const hasChartData = visibleRows.some((row) =>
    hasAnyValue(row, table?.periodKeys ?? [], mode),
  );

  const toggleLandsdelVisibility = (landsdel: string) => {
    setVisibleLandsdele((prev) =>
      prev.includes(landsdel)
        ? prev.filter((name) => name !== landsdel)
        : [...prev, landsdel],
    );
  };

  useEffect(() => {
    let cancelled = false;
    const kommunenrList = selectedMunicipalities.map(
      (municipality) => municipality.kommunenr,
    );
    if (!kommunenrList.length) {
      setGeometryPreview({ loading: false, error: null, imageUrl: null });
      return;
    }
    if (!mapStyle) {
      setGeometryPreview({
        loading: false,
        error: Localization.getText("Map style is not available."),
        imageUrl: null,
      });
      return;
    }

    setGeometryPreview((prev) => ({ ...prev, loading: true, error: null }));
    let objectUrl: string | null = null;
    ReferenceGeomDenmark.loadReferenceGeo()
      .then(async () => {
        if (cancelled) return;
        const features = getMunicipalityFeatures(kommunenrList);
        const imageBytes = await renderJPBLandsdelGeometryImage({
          mapStyle,
          features,
        });
        if (cancelled) return;
        if (!imageBytes) {
          setGeometryPreview({
            loading: false,
            error: Localization.getText("Map preview could not be rendered."),
            imageUrl: null,
          });
          return;
        }
        const imageBuffer = imageBytes.buffer.slice(
          imageBytes.byteOffset,
          imageBytes.byteOffset + imageBytes.byteLength,
        ) as ArrayBuffer;
        objectUrl = URL.createObjectURL(
          new Blob([imageBuffer], { type: "image/png" }),
        );
        setGeometryPreview({
          loading: false,
          error: null,
          imageUrl: objectUrl,
        });
      })
      .catch((error: any) => {
        if (cancelled) return;
        setGeometryPreview({
          loading: false,
          error: error?.message || String(error),
          imageUrl: null,
        });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mapStyle, selectedMunicipalities]);

  const exportExcel = () => {
    if (!table) return;
    const landsdelHeader = Localization.getText("Landsdel");
    const municipalitiesHeader = Localization.getText("Municipalities");
    const commonHeaders = [landsdelHeader, municipalitiesHeader];
    const buildRows = (exportMode: VisualizationMode) =>
      visibleRows.map((row) => {
        const municipalities = (
          municipalitiesByLandsdel.get(row.landsdel) ?? []
        )
          .map((municipality) => municipality.kommunenavn)
          .join(", ");
        const record: Record<string, string | number | boolean | null> = {
          [landsdelHeader]: row.landsdel,
          [municipalitiesHeader]: municipalities,
        };
        table.periodKeys.forEach((periodKey) => {
          record[periodKey] = getModeValue(row, periodKey, exportMode);
        });
        return record;
      });

    const wb = SheetFunc.createWorkBookFromJSON(
      {
        Title: Localization.getText("JPB Mølbak price index"),
        Author: "Mapit",
        CreatedDate: new Date(),
      },
      [
        {
          sheetName: "Index 100",
          headers: [...commonHeaders, ...table.periodKeys],
          rows: buildRows("factorIndex"),
        },
        {
          sheetName: Localization.getText("DKK per ha"),
          headers: [...commonHeaders, ...table.periodKeys],
          rows: buildRows("valueDkkHa"),
        },
      ],
    );
    ExportData.downloadBlob(
      `jpb-molbak-price-index-${Date.now()}.xlsx`,
      SheetFunc.createBlob(wb),
    );
  };

  const handleInfoClick = (
    event: MouseEvent<HTMLButtonElement>,
    landsdel: string,
  ) => {
    event.stopPropagation();
    setInfoLandsdel(landsdel);
  };

  const handleClose = () => windowDispatch(closeWindow(props.managedWindow.id));

  return (
    <AdvancedDragModal
      title={Localization.getText("Mølbak index")}
      window={props.managedWindow}
      onClose={handleClose}
      allowUserMiniMize={true}
      allowFullscreen={true}
      PositionToSave={{ left: true, top: true, width: true, height: true }}
      PosDefault={{
        left: "50%",
        top: "48%",
        transform: "translate(-50%, -50%)",
        width: "1180px",
        height: "860px",
      }}
    >
      <ADModalBody>
        <div style={styles.shell}>
          <section style={{ ...styles.hero, gridTemplateColumns: "1fr" }}>
            <div>
              <div style={styles.eyebrow}>
                {Localization.getText("JPB price index")}
              </div>
              <h2 style={styles.title}>
                {Localization.getText("Mølbak index")}
              </h2>
              <p style={styles.infoText}>
                {Localization.getText("info:molbakIndexIntro")}
              </p>
              <details style={{ ...styles.infoText, marginTop: 6 }}>
                <summary style={{ cursor: "pointer", fontWeight: 800 }}>
                  {Localization.getText("About the data basis")}
                </summary>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  <li>{Localization.getText("info:molbakIndexPoint1")}</li>
                  <li>{Localization.getText("info:molbakIndexPoint2")}</li>
                  <li>{Localization.getText("info:molbakIndexPoint3")}</li>
                </ul>
              </details>
            </div>
          </section>

          {loading && (
            <div style={styles.panel}>
              {Localization.getText("Updating...")}
            </div>
          )}
          {!loading && error && <div className="PropertyWarning">{error}</div>}
          {!loading && !error && table && (
            <>
              <section
                style={{
                  ...styles.panel,
                  order: 2,
                  flex: "0 0 auto",
                  minHeight: 450,
                  scrollMarginTop: 12,
                }}
              >
                <div style={styles.panelHeader}>
                  <div>
                    <div style={styles.eyebrow}>
                      {Localization.getText("Graph")}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: "#1f3935",
                      }}
                    >
                      {Localization.getText(
                        modes.find((item) => item.mode === mode)?.labelKey ??
                          "",
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: "#59716d" }}>
                      {Localization.getText(
                        modes.find((item) => item.mode === mode)
                          ?.descriptionKey ?? "",
                      )}
                    </div>
                  </div>
                </div>
                <div style={styles.chartWrap}>
                  <div style={styles.chartCanvas}>
                    {hasChartData ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={chartRows}
                          margin={{ top: 16, right: 28, bottom: 8, left: 18 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(47,83,78,0.16)"
                          />
                          <XAxis
                            dataKey="periodKey"
                            ticks={xTicks}
                            tick={{ fill: "#5c6f6b", fontSize: 12 }}
                            axisLine={{ stroke: "#9caeaa" }}
                          />
                          <YAxis
                            domain={yAxisDomain}
                            ticks={yAxisTicks}
                            tickFormatter={(value) =>
                              formatYAxisTick(Number(value), mode)
                            }
                            tick={{ fill: "#5c6f6b", fontSize: 12 }}
                            axisLine={{ stroke: "#9caeaa" }}
                          />
                          <Tooltip
                            formatter={(value: number, name: string) => [
                              formatCellValue(value, mode),
                              name,
                            ]}
                            contentStyle={{
                              borderRadius: 12,
                              border: "1px solid rgba(47,83,78,0.18)",
                            }}
                          />
                          <Legend />
                          {mode === "factorIndex" && (
                            <ReferenceLine
                              y={100}
                              stroke="#2d554f"
                              strokeDasharray="4 4"
                            />
                          )}
                          {visibleRows.map((row, idx) => (
                            <Line
                              key={row.landsdel}
                              type="monotone"
                              dataKey={row.landsdel}
                              name={row.landsdel}
                              stroke={
                                chartStrokePalette[
                                  idx % chartStrokePalette.length
                                ]
                              }
                              strokeWidth={2.4}
                              dot={false}
                              activeDot={{ r: 4 }}
                              connectNulls={false}
                            />
                          ))}
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <div
                        style={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#59716d",
                        }}
                      >
                        {Localization.getText("No graph lines selected.")}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <section style={styles.contentGrid}>
                <div style={styles.panel}>
                  <div style={styles.tableTools}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#59716d",
                        fontWeight: 700,
                      }}
                    >
                      {Localization.getFormattedText(
                        "Showing {visible} of {total} regions in the graph.",
                        {
                          visible: visibleRows.length,
                          total: table.rows.length,
                        },
                      )}
                    </div>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <div style={styles.modeSwitch}>
                        {modes.map((item) => (
                          <button
                            key={item.mode}
                            type="button"
                            onClick={() => setMode(item.mode)}
                            style={{
                              border: "none",
                              borderRadius: 999,
                              padding: "7px 12px",
                              fontSize: 12,
                              fontWeight: 800,
                              cursor: "pointer",
                              color: item.mode === mode ? "#fff" : "#294b46",
                              background:
                                item.mode === mode ? "#2d554f" : "transparent",
                            }}
                          >
                            {Localization.getText(item.labelKey)}
                          </button>
                        ))}
                      </div>
                      <GlassButton
                        onClick={exportExcel}
                        disabled={!table || loading}
                      >
                        {Localization.getText("Export Excel")}
                      </GlassButton>
                    </div>
                  </div>
                  <div style={styles.tableScroll}>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <Th sticky left={0}>
                            {Localization.getText("Landsdel")}
                          </Th>
                          {table.periodKeys.map((periodKey) => (
                            <Th key={periodKey}>{periodKey}</Th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableRows.map((row) => {
                          const isVisible = visibleLandsdele.includes(
                            row.landsdel,
                          );
                          const rowHasValues = hasAnyValue(
                            row,
                            table.periodKeys,
                            mode,
                          );
                          return (
                            <tr
                              key={row.landsdel}
                              onClick={() =>
                                rowHasValues &&
                                toggleLandsdelVisibility(row.landsdel)
                              }
                              style={{
                                cursor: rowHasValues ? "pointer" : "default",
                                opacity: isVisible ? 1 : 0.42,
                              }}
                            >
                              <Td sticky left={0}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      color: isVisible ? "#2d554f" : "#9aa8a5",
                                      fontWeight: 900,
                                      fontSize: 14,
                                    }}
                                  >
                                    {isVisible ? "●" : "○"}
                                  </span>
                                  <span style={{ fontWeight: 800 }}>
                                    {row.landsdel}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(event) =>
                                      handleInfoClick(event, row.landsdel)
                                    }
                                    style={{
                                      border: "1px solid rgba(47,83,78,0.22)",
                                      background:
                                        selectedInfoRow?.landsdel ===
                                        row.landsdel
                                          ? "#2d554f"
                                          : "#fff",
                                      color:
                                        selectedInfoRow?.landsdel ===
                                        row.landsdel
                                          ? "#fff"
                                          : "#2d554f",
                                      borderRadius: 999,
                                      padding: "0 9px",
                                      minWidth: 38,
                                      height: 22,
                                      fontSize: 11,
                                      fontWeight: 800,
                                      lineHeight: "20px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    {Localization.getText("Info")}
                                  </button>
                                </div>
                              </Td>
                              {table.periodKeys.map((periodKey) => (
                                <Td key={`${row.landsdel}-${periodKey}`}>
                                  {formatCellValue(
                                    getModeValue(row, periodKey, mode),
                                    mode,
                                  )}
                                </Td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <aside style={styles.sideCard}>
                  <div style={{ ...styles.eyebrow, marginBottom: 6 }}>
                    {Localization.getText("Landsdel info")}
                  </div>
                  <h3 style={{ margin: 0, color: "#1f3935" }}>
                    {selectedInfoRow?.landsdel ??
                      Localization.getText("Select a landsdel")}
                  </h3>
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: "#59716d",
                      lineHeight: 1.45,
                    }}
                  >
                    {Localization.getFormattedText(
                      "info:jpbIndexSelectedMunicipalities",
                      { count: selectedMunicipalities.length },
                    )}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      flexWrap: "wrap",
                      marginTop: 12,
                    }}
                  >
                    {selectedMunicipalities.map((municipality) => (
                      <span
                        key={`${municipality.kommunenr}-${municipality.kommunenavn}`}
                        style={{
                          borderRadius: 999,
                          padding: "5px 8px",
                          background: "rgba(47,83,78,0.09)",
                          color: "#294b46",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {municipality.kommunenavn}
                      </span>
                    ))}
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      paddingTop: 12,
                      borderTop: "1px solid rgba(47,83,78,0.12)",
                    }}
                  >
                    <div style={{ ...styles.eyebrow, marginBottom: 8 }}>
                      {Localization.getText("Geographic overview")}
                    </div>
                    <GeometryPreview preview={geometryPreview} />
                  </div>
                </aside>
              </section>
            </>
          )}
        </div>
      </ADModalBody>
      <ADModalFooter>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <GlassButton onClick={handleClose}>
            {Localization.getText("Close")}
          </GlassButton>
        </div>
      </ADModalFooter>
    </AdvancedDragModal>
  );
}

function GeometryPreview(props: { preview: GeometryPreviewState }) {
  const height = 150;

  if (props.preview.loading) {
    return (
      <div
        style={{
          height,
          borderRadius: 12,
          background: "rgba(47,83,78,0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#59716d",
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {Localization.getText("Loading geometry...")}
      </div>
    );
  }

  if (props.preview.error || !props.preview.imageUrl) {
    return (
      <div
        style={{
          height,
          borderRadius: 12,
          background: "rgba(47,83,78,0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#59716d",
          fontSize: 12,
          textAlign: "center",
          padding: 12,
        }}
      >
        {Localization.getText("The map could not be shown.")}
      </div>
    );
  }

  return (
    <img
      src={props.preview.imageUrl}
      width="100%"
      height={height}
      alt={Localization.getText("Miniature map of the landsdel municipalities")}
      style={{
        display: "block",
        borderRadius: 12,
        border: "1px solid rgba(47,83,78,0.14)",
        objectFit: "cover",
      }}
    />
  );
}

function Th(props: { children: ReactNode; sticky?: boolean; left?: number }) {
  return (
    <th
      style={{
        position: "sticky",
        left: props.sticky ? (props.left ?? 0) : undefined,
        top: 0,
        zIndex: props.sticky ? 4 : 3,
        background: "#eef4f2",
        color: "#294b46",
        textAlign: "left",
        padding: "10px 12px",
        borderBottom: "1px solid rgba(47,83,78,0.14)",
        whiteSpace: "nowrap",
        boxShadow: props.sticky
          ? "8px 0 12px rgba(255,255,255,0.92)"
          : undefined,
      }}
    >
      {props.children}
    </th>
  );
}

function Td(props: { children: ReactNode; sticky?: boolean; left?: number }) {
  return (
    <td
      style={{
        position: props.sticky ? "sticky" : undefined,
        left: props.sticky ? (props.left ?? 0) : undefined,
        zIndex: props.sticky ? 2 : 1,
        background: props.sticky ? "#fff" : undefined,
        padding: "9px 12px",
        borderBottom: "1px solid rgba(47,83,78,0.08)",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {props.children}
    </td>
  );
}
